import React, { Component, Fragment } from 'react';
import { inject, observer } from 'mobx-react';
import { Link, withRouter } from '../router-compat';
import { Affix, Badge, Input, Menu, Popover } from 'antd';
import { AlertCircle, ChevronDown, Plus, Shield, Search, Settings } from 'react-feather';
import { withTranslation } from 'react-i18next';
import styled from 'styled-components';
import { makeObservable, observable } from 'mobx';

import { Colors, Metrics } from '../theme';
import Logo from './logo';
import IncompleteAccount from './incomplete-account';
import HasPermission from '../components/has-permission';
import PopoverTrigger from './popover-trigger';
import Store from '../assets/svg/icons/store.svg';
import { media } from '../utils';
import config from '../config';

const Container = styled.div`
  align-items: center;
  background: white;
  box-shadow: ${Colors.shadows.primary};
  display: flex;
  padding: 1.125em 2em;
  position: relative;
  width: 100%;
  z-index: 20;
`;

const LogoContainer = styled(Link)`
  cursor: pointer;
  margin-right: auto;

  > div {
    @media (max-width: 480px) {
      padding-left: .75rem;
      width: 2rem;
      overflow: hidden;
    }
  }
`;

const DropdownTrigger = styled.span`
  cursor: pointer;
  line-height: 2.5em;
`;

const Avatar = styled.div`
  background-color: #FAFAFA;
  box-shadow: ${Colors.shadows.primary};
  border-radius: 50%;
  cursor: pointer;
  height: 2rem;
  line-height: 2.125rem;
  margin-left: 1rem;
  position: relative;
  text-align: center;
  transition: box-shadow 250ms;
  width: 2rem;

  &:hover {
    box-shadow: ${Colors.shadows.hover};
  }
`;

const LanguagePicker = styled.img`
  cursor: pointer;
  height: 1em;
  margin-left: 1em;
  width: auto;
`;

const Flag = styled.img`
  height: 1em;
  margin-right: 1em;
  vertical-align: middle;
  width: auto;
`;

const ApprovalBanner = styled.div`
  align-items: center;
  background-color: ${Colors.lighten(Colors.brand.primary, 35)};
  border-bottom: 1px solid rgba(0, 0, 0, .025);
  display: flex;
  padding: 0.75rem 2rem 0.75rem 1rem;
  position: relative;
  z-index: 1;

  svg {
    color: ${Colors.brand.primary};
  }

  p {
    display: flex;
    flex: 1;
    margin: 0 auto;
    max-width: calc(${Metrics.maxWidth}px - 5em);
  }
`;

const AdminLink = styled(Link)`
  padding-left: 2em;
`;

const OrgMenu = styled(Menu)`
  && .ant-menu-item,
  && .ant-menu-item-group-list .ant-menu-item {
    padding-inline: 16px;
  }

  /* items are inset 4px by their margin-inline; match their left edge */
  .ant-menu-item-group-title {
    padding-inline: 20px;
  }

  .ant-menu-title-content a {
    align-items: center;
    display: flex;
    gap: .75em;
  }

  && .ant-menu-item--search {
    cursor: default;

    &, &:hover, &:active, &.ant-menu-item-active {
      background: none !important;
    }
  }
`;

const SearchContainer = styled.div`
  cursor: default !important;
  padding: 0.5em 0;
`;

const SearchField = styled(Input)`
  border-color: rgba(0, 0, 0, .05);
  background-color: rgba(0, 0, 0, .025);
  height: 2.75em;
  padding: 0 .5em;

  &.ant-input-affix-wrapper-focused,
  &.ant-input-affix-wrapper:hover {
    border-color: rgba(0, 0, 0, .05);
    outline: none;
  }

  &:hover, &:focus {
    background-color: rgba(0, 0, 0, .05);
    outline: none;
  }

  .ant-input {
    background-color: transparent !important;
    box-shadow: none !important;
    padding-left: 0 !important;
  }
`;

const Toolbar = styled.div.attrs({ id: 'toolbar-portal' })`
  align-items: center;
  display: flex;
  flex: 1;
  height: 2.5em;

  &:empty {
    display: none;
  }
`;

@inject('app', 'uiStore')
@withRouter
@observer
class TopNav extends Component {
  constructor (...args) {
    super(...args);
    makeObservable(this);
  }

  @observable searchQuery = '';

  get filteredOrganizations () {
    const { user, organization } = this.props.app;
    const otherOrganizations = user.organizations
      .filter(mer => mer.id !== organization.id);

    if (this.searchQuery.trim()) {
      return otherOrganizations.filter(org => {
        return org.name.toLowerCase().includes(this.searchQuery.toLowerCase());
      });
    }

    return otherOrganizations;
  }

  handleSearch = e => {
    this.searchQuery = e.target.value;
  };

  changeLanguage = lang => () => {
    const { i18n, uiStore } = this.props;
    const languageCode = lang.split('-')[0];

    i18n.changeLanguage(lang);
    uiStore.language = languageCode;
  };

  logout = async () => {
    await this.props.app.logout();

    setTimeout(() => {
      this.props.uiStore.updateReferrer(null);
      window.location.reload();
    }, 250);
  };

  renderOrganization = () => {
    const { app, t, uiStore } = this.props;
    const { user, organization } = app;
    const limit = 5;

    if (!user) {
      return null;
    }

    const otherOrganizations = user.organizations
      .filter(mer => mer.id !== organization.id);

    const orgsNotVisible = otherOrganizations.length - limit;

    const orgMenuItems = [
      {
        key: 'settings',
        label: (
          <Link to={`/organization/${organization.id}/settings_business`}>
            <Settings size={16} /> {t('topNav.teamSettings')}
          </Link>
        )
      },
      { type: 'divider' },
      ...(user.organizations.length > 1
        ? [{
            type: 'group',
            label: t('topNav.switchOrg'),
            children: [
              ...(otherOrganizations.length > limit
                ? [{
                    key: 'search-container',
                    className: 'ant-menu-item--search',
                    style: { height: 'auto', lineHeight: 'normal' },
                    label: (
                      <SearchContainer onClick={e => e.stopPropagation()}>
                        <SearchField
                          onChange={this.handleSearch}
                          prefix={<Search color={Colors.typography.secondary} size={16} />}
                          placeholder={t('topNav.searchPlaceholder', { count: orgsNotVisible })}
                          type="search"
                          value={this.searchQuery} />
                      </SearchContainer>
                    )
                  }]
                : []),
              ...this.filteredOrganizations.slice(0, limit).map(org => ({
                key: org.id,
                label: <Link to={`/organization/${org.id}/charges`} onClick={uiStore.resetState}>{org.name}</Link>
              })),
              ...(this.filteredOrganizations.length === 0 && !!this.searchQuery.trim()
                ? [{ disabled: true, key: 'no-results', label: t('shared.pagination_noEntries') }]
                : [])
            ]
          }]
        : []),
      { type: 'divider' },
      {
        key: 'create',
        label: <Link to="/onboarding"><Plus size={16} /> {t('topNav.newOrg')}</Link>
      }
    ];

    const orgMenu = (
      <OrgMenu
        items={orgMenuItems}
        onClick={({ domEvent }) => domEvent.stopPropagation()} />
    );

    return (
      <Popover trigger="click" content={orgMenu} placement="bottomRight">
        <PopoverTrigger>
          <DropdownTrigger>
            {media.mobile &&
              <span><Store width={16} /> <ChevronDown size={16} /></span>
            }
            {!media.mobile &&
              <span>{organization.name} <ChevronDown size={16} /></span>
            }
          </DropdownTrigger>
        </PopoverTrigger>
      </Popover>
    );
  };

  renderAccountDropDown = () => {
    const { app, t } = this.props;
    const { user } = app;
    const name = user.name.split(' ');
    const firstName = name[0];
    const lastName = name.length > 1 ? name[1] : '';

    if (!user) return null;

    const userMenu = (
      <Menu
        items={[
          ...(user.pendingInvites && !!user.pendingInvites.length
            ? [{
                className: 'ant-dropdown-menu-item--info',
                disabled: true,
                key: 'invites',
                label: t('topNav.pendingInvites', { count: user.pendingInvites.length })
              }]
            : []),
          { key: 'account', label: <Link to="/account">{t('topNav.account')}</Link> },
          { type: 'divider' },
          { key: 'logout', label: <a onClick={this.logout}>{t('topNav.signOut')}</a> }
        ]} />
    );

    return (
      <Popover trigger="click" content={userMenu} placement="bottomRight">
        <PopoverTrigger>
          <Badge count={user.pendingInvites && user.pendingInvites.length}>
            <Avatar>{firstName.slice(0, 1)}{lastName.slice(0, 1)}</Avatar>
          </Badge>
        </PopoverTrigger>
      </Popover>
    );
  };

  renderLanguageSelector = () => {
    const { uiStore } = this.props;

    const languageMenu = (
      <Menu
        items={[{
          key: 'en-US',
          label: (
            <a onClick={this.changeLanguage('en-US')}>
              <Flag src="/images/flags/en-US.svg" /> English
            </a>
          )
        }, {
          key: 'es-ES',
          label: (
            <a onClick={this.changeLanguage('es-ES')}>
              <Flag src="/images/flags/es-ES.svg" /> Español
            </a>
          )
        }]} />
    );

    return (
      <Popover trigger="click" content={languageMenu}>
        <PopoverTrigger>
          <LanguagePicker src={`/images/flags/${uiStore.language}.svg`} />
        </PopoverTrigger>
      </Popover>
    );
  };

  renderAdmin = () => {
    const { isAdmin, networkInfo } = this.props.app;
    let badgeCount = null;
    if (isAdmin && networkInfo) {
      badgeCount = networkInfo.ethBehindBlockCount;
    }
    const status = badgeCount && badgeCount > 0 ? 'error' : null;
    const adminLink = isAdmin ? '/admin/nodes' : '/admin/users';
    return (
      <HasPermission permissions={config.permissions.reseller}>
        <Badge title={badgeCount} status={status}>
          <AdminLink to={adminLink}><Shield /></AdminLink>
        </Badge>
      </HasPermission>
    );
  };

  render () {
    const { app, t } = this.props;
    const { isAuthenticated, organization } = app;

    if (!isAuthenticated) return null;

    return (
      <Affix>
        <div>
          <Container>
            <LogoContainer to="/">
              <Logo style={{ margin: 0 }} logoStyle={{ margin: 0 }} size="small" />
            </LogoContainer>
            <Toolbar />
            {organization && this.renderOrganization()}
            {this.renderAccountDropDown()}
            {this.renderAdmin()}
            {this.renderLanguageSelector()}
          </Container>
          {!location.pathname.includes('/admin') &&
            <Fragment>
              {organization && !organization.approvedAt &&
                <ApprovalBanner>
                  <p>
                    <AlertCircle size={20} style={{ marginRight: '1em' }} />
                    {t(!media.mobile ? 'topNav.approvalRequired' : 'topNav.approvalRequiredMobile')}
                  </p>
                </ApprovalBanner>
              }
              {organization && <div><IncompleteAccount organizationId={app.organization.id} /></div>}
            </Fragment>
          }
        </div>
      </Affix>
    );
  }
}

export default withTranslation('translations')(TopNav);
